/**
 * AudioContextOwner — the sole browser audio-device owner for one engine.
 *
 * Creating an AudioContext can initialise the host audio device and may also
 * trigger autoplay-policy warnings when it happens before a user gesture.
 * Callers may preload bytes/modules ahead of time, but the context itself is
 * created only when resume() is invoked from an accepted gesture.
 */
export class AudioContextOwner {
  private context: AudioContext | null = null;
  private closed = false;

  /** Return the context without constructing one. */
  current(): AudioContext | null {
    return this.context;
  }

  /**
   * Construct the shared context. Product callers must reach this through
   * resume() from a user gesture; the method stays public for engine graph
   * construction after that unlock has begun.
   */
  getOrCreate(): AudioContext {
    if (this.context) return this.context;
    if (this.closed) throw new Error('AudioContextOwner is closed');

    const scope = globalThis as typeof globalThis & {
      AudioContext?: typeof AudioContext;
      webkitAudioContext?: typeof AudioContext;
    };
    const Constructor = scope.AudioContext ?? scope.webkitAudioContext;
    if (!Constructor) throw new Error('Web Audio is not available');

    this.context = new Constructor();
    return this.context;
  }

  /** Create and unlock the context. Must be called from a user gesture. */
  async resume(): Promise<AudioContext> {
    const context = this.getOrCreate();
    if (context.state !== 'running') await context.resume();
    return context;
  }

  /** Idempotently close the owned device. */
  async close(): Promise<void> {
    if (this.closed) return;
    this.closed = true;
    const context = this.context;
    this.context = null;
    if (context && context.state !== 'closed') {
      await context.close().catch(() => undefined);
    }
  }
}
